import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import MediaCard from '../components/MediaCard';
import Lightbox from '../components/Lightbox';
import ShareManager from '../components/ShareManager';
import Modal from '../components/Modal';
import { useSocket } from '../hooks/useSocket';
import './EventDetailPage.css';

const PAGE_SIZE = 30;

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const socket = useSocket();

  const [event, setEvent] = useState(null);
  const [eventError, setEventError] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const sentinelRef = useRef(null);

  // ── Modal state ──────────────────────────────────────────────────────────
  const [modal, setModal] = useState(null); // { title, message, type, danger, onConfirm }
  function closeModal() { setModal(null); }
  function showConfirm({ title, message, danger, confirmLabel, onConfirm }) {
    setModal({ title, message, type: 'confirm', danger, confirmLabel, onConfirm });
  }
  function showAlert({ title, message, type = 'error' }) {
    setModal({ title, message, type, onConfirm: closeModal });
  }

  // Load event metadata
  useEffect(() => {
    api.get(`/api/events/${eventId}`)
      .then(({ data }) => setEvent(data.event))
      .catch(() => setEventError('Event not found'));
  }, [eventId]);

  const fetchPage = useCallback(async (pageNum, append = false) => {
    try {
      const { data } = await api.get(`/api/media?eventId=${eventId}&page=${pageNum}&limit=${PAGE_SIZE}`);
      setItems((prev) => append ? [...prev, ...data.items] : data.items);
      setTotalPages(data.pagination.totalPages);
    } catch {
      // silent
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [eventId]);

  useEffect(() => {
    setLoading(true);
    fetchPage(1);
  }, [fetchPage]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingMore && page < totalPages) {
        const next = page + 1;
        setPage(next);
        setLoadingMore(true);
        fetchPage(next, true);
      }
    }, { rootMargin: '400px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [fetchPage, loadingMore, page, totalPages]);

  // ── Socket: real-time processing updates ─────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    function onMediaReady(data) {
      // Only update items belonging to this event
      if (data.eventId !== eventId) return;
      setItems((prev) => prev.map((item) =>
        item.id === data.id ? { ...item, ...data } : item
      ));
    }

    function onMediaFailed(data) {
      if (data.eventId !== eventId) return;
      setItems((prev) => prev.map((item) =>
        item.id === data.id ? { ...item, status: 'failed' } : item
      ));
    }

    socket.on('media:ready', onMediaReady);
    socket.on('media:failed', onMediaFailed);
    return () => {
      socket.off('media:ready', onMediaReady);
      socket.off('media:failed', onMediaFailed);
    };
  }, [socket, eventId]);

  // ── Actions ───────────────────────────────────────────────────────────────
  function handleDelete(item) {
    showConfirm({
      title: 'Delete file',
      message: `Delete "${item.originalName}"? This cannot be undone.`,
      danger: true,
      onConfirm: async () => {
        closeModal();
        try {
          await api.delete(`/api/media/${item.id}`);
          setItems((prev) => prev.filter((i) => i.id !== item.id));
          setEvent((ev) => ev ? { ...ev, mediaCount: Math.max(0, ev.mediaCount - 1) } : ev);
        } catch {
          showAlert({ title: 'Delete failed', message: 'Could not delete the file. Please try again.' });
        }
      },
    });
  }

  function handleDeleteEvent() {
    showConfirm({
      title: `Delete event`,
      message: `Delete "${event?.name}"? This will permanently delete all ${event?.mediaCount || 0} files in this event from storage. This cannot be undone.`,
      danger: true,
      confirmLabel: 'Delete event & all files',
      onConfirm: async () => {
        closeModal();
        try {
          await api.delete(`/api/events/${eventId}`);
          navigate('/events');
        } catch {
          showAlert({ title: 'Delete failed', message: 'Could not delete the event. Please try again.' });
        }
      },
    });
  }

  if (eventError) {
    return (
      <div className="ed-error">
        <p>{eventError}</p>
        <Link to="/events" className="btn btn-ghost">← Back to events</Link>
      </div>
    );
  }

  return (
    <div className="ed-page page-wrapper">
      <div className="ed-header">
        <div className="ed-header-left">
          <Link to="/events" className="ed-back">← Events</Link>
          <div className="ed-title-row">
            {event && <span className="ed-color-dot" style={{ background: event.coverColor }} />}
            <h1 className="ed-title">{event?.name || '…'}</h1>
          </div>
          {event?.description && <p className="ed-desc">{event.description}</p>}
        </div>

        <div className="ed-header-actions">
          <button className="btn btn-ghost" onClick={() => setShowShare(true)}>Share</button>
          <button className="btn btn-ghost" onClick={() => setShowEditModal(true)}>Edit</button>
          <Link to={`/upload?eventId=${eventId}`} className="btn btn-primary">+ Upload</Link>
        </div>
      </div>

      {loading ? (
        <div className="ed-loading"><div className="spinner" /></div>
      ) : items.length === 0 ? (
        <div className="ed-empty">
          <span>☁</span>
          <p>No media in this event yet</p>
          <Link to={`/upload?eventId=${eventId}`} className="btn btn-primary">Upload files</Link>
        </div>
      ) : (
        <>
          <div className="gallery-grid">
            {items.map((item) => (
              <MediaCard
                key={item.id}
                item={item}
                onClick={(it) => setSelectedId(it.id)}
                onDelete={handleDelete}
              />
            ))}
          </div>
          <div ref={sentinelRef} className="gallery-sentinel">
            {loadingMore && <div className="spinner" />}
          </div>
        </>
      )}

      <div className="ed-danger">
        <button className="btn btn-danger btn-sm" onClick={handleDeleteEvent}>Delete event</button>
        <span className="ed-danger-note">Permanently deletes the event and all its files.</span>
      </div>

      {selectedId && <Lightbox mediaId={selectedId} onClose={() => setSelectedId(null)} />}

      {showShare && event && (
        <ShareManager eventId={eventId} eventName={event.name} onClose={() => setShowShare(false)} />
      )}

      {showEditModal && event && (
        <EditEventModal
          event={event}
          onClose={() => setShowEditModal(false)}
          onSaved={(updated) => setEvent((ev) => ({ ...ev, ...updated }))}
        />
      )}

      {modal && (
        <Modal
          title={modal.title}
          message={modal.message}
          type={modal.type}
          danger={modal.danger}
          confirmLabel={modal.confirmLabel}
          onConfirm={modal.onConfirm}
          onCancel={closeModal}
        />
      )}
    </div>
  );
}

// ── Edit event modal (unchanged) ──────────────────────────────────────────────
const ACCENT_COLORS = [
  '#6c8fff', '#e87461', '#4caf82', '#f58220',
  '#a78bfa', '#34d399', '#f472b6', '#60a5fa',
];

function EditEventModal({ event, onClose, onSaved }) {
  const [name, setName] = useState(event.name);
  const [description, setDescription] = useState(event.description || '');
  const [coverColor, setCoverColor] = useState(event.coverColor || ACCENT_COLORS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return setError('Name is required');
    setLoading(true);
    setError('');
    try {
      const { data } = await api.patch(`/api/events/${event.id}`, { name, description, coverColor });
      onSaved(data.event);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update event');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-header">
          <h2>Edit event</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="banner banner-error" style={{ margin: '0 1.5rem' }}>{error}</div>}
        <form onSubmit={handleSubmit} className="modal-form">
          <label className="modal-label">
            Event name *
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </label>
          <label className="modal-label">
            Description
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </label>
          <div className="modal-label">
            Accent color
            <div className="color-picker">
              {ACCENT_COLORS.map((c) => (
                <button key={c} type="button"
                  className={`color-swatch ${coverColor === c ? 'color-swatch--active' : ''}`}
                  style={{ background: c }} onClick={() => setCoverColor(c)} />
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
